/**
 * SF-4651: the wizard's checks, one function per step, so each step and its tests share them.
 * Each returns a plain reason when the answer is refused, or the cleaned value.
 */
import {
  MAX_AREA_LABEL_LENGTH,
  MAX_MODULE_KEYS,
  MAX_NOTES_LENGTH,
  MAX_RADIUS_MILES,
  MAX_SITES,
  MAX_TRANSCRIPT_BYTES,
  MAX_TRANSCRIPT_FILES,
  TRANSCRIPT_EXTENSIONS,
} from './constants';

export type Checked<T> = { value: T; error?: undefined } | { value?: undefined; error: string };

// One DNS label: letters, digits and inner hyphens, at most 63 characters.
const LABEL_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
// A real top-level domain is letters only (or an IDN "xn--" label), at least two long.
const TLD_RE = /^([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

/**
 * Step 1: the prospect's web address, as a host name ("greggs.co.uk").
 * Accepts a pasted link (scheme, www., path) and keeps only the host. Refuses anything that
 * cannot be a public web address: no dot, a bad label, a made-up ending, an IP address,
 * localhost, or an email address.
 */
export function checkWebAddress(raw: string): Checked<string> {
  const text = raw.trim();
  if (!text) return { error: 'Enter the prospect’s web address' };
  if (text.includes('@')) return { error: 'Enter a web address, not an email address' };
  if (/\s/.test(text)) return { error: 'A web address has no spaces' };
  let host: string;
  try {
    host = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`).hostname.toLowerCase();
  } catch {
    return { error: 'That is not a web address, for example greggs.co.uk' };
  }
  host = host.replace(/\.$/, '');
  if (host.startsWith('www.')) host = host.slice(4);
  const labels = host.split('.');
  if (labels.length < 2 || !labels.every((l) => LABEL_RE.test(l))) {
    return { error: 'That is not a web address, for example greggs.co.uk' };
  }
  if (!TLD_RE.test(labels[labels.length - 1])) {
    return { error: `“.${labels[labels.length - 1]}” is not a real web address ending` };
  }
  if (host.length > 253) return { error: 'That web address is too long' };
  return { value: host };
}

export interface AreaAnswer {
  label?: string;
  lat: number | null;
  lng: number | null;
  radius_miles: number | null;
  max_sites: number | null;
}

export interface CheckedArea {
  label?: string;
  lat: number;
  lng: number;
  radius_miles: number;
  max_sites: number;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Step 2: a centre (from a town/postcode or a map point), a radius and a number of locations. */
export function checkArea(a: AreaAnswer): Checked<CheckedArea> {
  if (!isNum(a.lat) || !isNum(a.lng) || Math.abs(a.lat) > 90 || Math.abs(a.lng) > 180) {
    return { error: 'Find a town or postcode, or pick a point on the map' };
  }
  if (!isNum(a.radius_miles) || a.radius_miles <= 0 || a.radius_miles > MAX_RADIUS_MILES) {
    return { error: `The radius must be more than 0 and at most ${MAX_RADIUS_MILES} miles` };
  }
  if (
    !Number.isInteger(a.max_sites) ||
    (a.max_sites as number) < 1 ||
    (a.max_sites as number) > MAX_SITES
  ) {
    return { error: `The number of locations must be a whole number from 1 to ${MAX_SITES}` };
  }
  const label = a.label?.trim().slice(0, MAX_AREA_LABEL_LENGTH);
  return {
    value: {
      ...(label ? { label } : {}),
      lat: a.lat,
      lng: a.lng,
      radius_miles: a.radius_miles,
      max_sites: a.max_sites as number,
    },
  };
}

/** Step 3 notes: free text, optional. */
export function checkNotes(notes: string): Checked<string> {
  if (notes.length > MAX_NOTES_LENGTH) {
    return { error: `Notes can be at most ${MAX_NOTES_LENGTH} characters` };
  }
  return { value: notes.trim() };
}

/** SF-4847 Modules step. `keys` is kept while "Let Eva decide" is picked, so going back keeps them. */
export interface ModulesAnswer {
  mode: 'eva_decides' | 'chosen';
  keys: string[];
}

/**
 * The Modules step. "Choose modules" needs at least one of the offered modules; keys that are not
 * offered (a list that changed) are dropped. `offered` is null when the list could not be read,
 * and Eva then decides.
 */
export function checkModules(
  answer: ModulesAnswer,
  offered: string[] | null,
): Checked<{ mode: 'eva_decides' } | { mode: 'chosen'; keys: string[] }> {
  if (answer.mode === 'eva_decides' || !offered) return { value: { mode: 'eva_decides' } };
  const keys = offered.filter((k) => answer.keys.includes(k));
  if (keys.length === 0) return { error: 'Pick at least one module, or let Eva decide' };
  if (keys.length > MAX_MODULE_KEYS) {
    return { error: `Pick at most ${MAX_MODULE_KEYS} modules` };
  }
  return { value: { mode: 'chosen', keys } };
}

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase();
};

/**
 * Step 3 transcripts: why this file is refused before upload, or null when it may be sent.
 * `alreadyChosen` is the number of files kept so far (the sixth and later are refused).
 */
export function transcriptRefusal(
  file: { name: string; size: number },
  alreadyChosen: number,
): string | null {
  if (alreadyChosen >= MAX_TRANSCRIPT_FILES) {
    return `Only ${MAX_TRANSCRIPT_FILES} transcript files can be added`;
  }
  if (!(TRANSCRIPT_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return `Not a transcript file type (use ${TRANSCRIPT_EXTENSIONS.join(' ')})`;
  }
  if (file.size === 0) return 'The file is empty';
  if (file.size > MAX_TRANSCRIPT_BYTES) return 'The file is bigger than 10 MB';
  return null;
}
